/**
 * The salary PIN steps (ADR 0041), shared by the prompt (`SalaryPinDialog`) and the settings card
 * (`SalaryPinSettingsCard`): create, enter, locked, change, and forgot-by-phone. Each step reports the
 * one outcome its parent needs — an `unlockedUntil` on success, a `lockedUntil` when the PIN locks — and
 * keeps its own field state.
 *
 * Every accepted PIN unlocks this login session on the server; the parent hands the instant on to
 * `resolveUnlock` so the web's relock timer matches. No PIN is ever logged, stored or kept past submit.
 */

import { useState, type FormEvent } from 'react';
import { Lock } from 'lucide-react';
import { Banner, Button, CountdownTimer } from '@hrms/ui';
import { SALARY_PIN_MAX_ATTEMPTS, type SalaryPinStatus } from '@hrms/shared';
import { tsr } from '../../lib/api.js';
import { SalaryPinField } from './SalaryPinField.js';
import { maskedPinPhone, SalaryPinPhoneCode } from './SalaryPinPhoneCode.js';
import { localUnlockEnd } from './salaryPinGate.js';
import { NewPinFields, newPinProblem, PinActions as Actions } from './SalaryPinNewPinFields.js';
import { SalaryPinNewPhoneFlow } from './SalaryPinNewPhoneFlow.js';
import {
  errorReason,
  salaryPinErrorMessage,
  salaryPinRequiredDetails,
  wrongPinAttemptsRemaining,
} from './salaryPinErrors.js';

/** First time: choose a PIN, twice. */
export function SalaryPinCreateForm({
  onUnlocked,
  onCancel,
}: {
  onUnlocked: (unlockedUntil: string) => void;
  onCancel?: () => void;
}) {
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const setPinMutation = tsr.salaryPin.setPin.useMutation();

  function submit(event: FormEvent) {
    event.preventDefault();
    const issue = newPinProblem(pin, confirm);
    if (issue) return setProblem(issue);
    setProblem(null);
    setPinMutation.mutate(
      { body: { pin } },
      {
        onSuccess: (result) => {
          if (result.status === 200) onUnlocked(localUnlockEnd(result.body.unlockedForMs));
        },
        onError: (error) => {
          setConfirm('');
          setProblem(salaryPinErrorMessage(error, 'Could not save your PIN. Please try again.'));
        },
      },
    );
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      <p className="text-sm text-fg-muted">
        Pay details are protected by a personal PIN. Choose one now — you will enter it to view
        salaries, payslips and payroll, and it stays unlocked for five minutes at a time.
      </p>
      <NewPinFields
        pin={pin}
        confirm={confirm}
        onPin={(value) => {
          setPin(value);
          setProblem(null);
        }}
        onConfirm={(value) => {
          setConfirm(value);
          setProblem(null);
        }}
        problem={problem}
        disabled={setPinMutation.isPending}
      />
      <Actions>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" loading={setPinMutation.isPending}>
          Create PIN
        </Button>
      </Actions>
    </form>
  );
}

/** The everyday step: enter the PIN. A wrong one says how many tries are left. */
export function SalaryPinEnterForm({
  attemptsRemaining,
  onUnlocked,
  onLocked,
  onForgot,
  onCancel,
}: {
  /** From the status, until a wrong PIN replaces it. */
  attemptsRemaining: number;
  onUnlocked: (unlockedUntil: string) => void;
  onLocked: (lockedUntil: string | null) => void;
  onForgot?: () => void;
  onCancel?: () => void;
}) {
  const [pin, setPin] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const verify = tsr.salaryPin.verifyPin.useMutation();

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!/^\d{4,6}$/.test(pin)) return setProblem('Enter your 4 to 6 digit PIN');
    setProblem(null);
    verify.mutate(
      { body: { pin } },
      {
        onSuccess: (result) => {
          if (result.status === 200) onUnlocked(localUnlockEnd(result.body.unlockedForMs));
        },
        onError: (error) => {
          setPin('');
          const remaining = wrongPinAttemptsRemaining(error);
          if (remaining !== null) {
            setProblem(`Wrong PIN — ${remaining} ${remaining === 1 ? 'try' : 'tries'} left`);
            return;
          }
          const refusal = salaryPinRequiredDetails(error);
          if (refusal?.reason === 'locked') return onLocked(refusal.lockedUntil ?? null);
          setProblem(salaryPinErrorMessage(error, 'Could not check your PIN. Please try again.'));
        },
      },
    );
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      <SalaryPinField
        label="Salary PIN"
        name="pin"
        value={pin}
        onChange={(value) => {
          setPin(value);
          setProblem(null);
        }}
        error={problem}
        hint={
          !problem && attemptsRemaining < SALARY_PIN_MAX_ATTEMPTS
            ? `${attemptsRemaining} ${attemptsRemaining === 1 ? 'try' : 'tries'} left before the PIN locks`
            : undefined
        }
        autoFocus
        disabled={verify.isPending}
      />
      <Actions>
        {onForgot && (
          <Button type="button" variant="ghost" className="mr-auto" onClick={onForgot}>
            Forgot PIN?
          </Button>
        )}
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" loading={verify.isPending}>
          Unlock
        </Button>
      </Actions>
    </form>
  );
}

/** Too many wrong PINs: a countdown to when the PIN can be tried again. */
export function SalaryPinLockedNotice({
  lockedUntil,
  onExpire,
  onForgot,
  onCancel,
}: {
  lockedUntil: string | null;
  onExpire: () => void;
  onForgot?: () => void;
  onCancel?: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <Banner tone="danger" icon={<Lock className="size-4" aria-hidden="true" />}>
        Too many wrong PINs. Your salary PIN is locked for a few minutes.
      </Banner>
      {lockedUntil && (
        <p className="flex items-center gap-2 text-sm text-fg-muted">
          Try again in
          <CountdownTimer
            deadline={new Date(lockedUntil)}
            ariaLabel="Time until you can try your PIN again"
            onExpire={onExpire}
          />
        </p>
      )}
      <Actions>
        {onForgot && (
          <Button type="button" variant="ghost" className="mr-auto" onClick={onForgot}>
            Forgot PIN?
          </Button>
        )}
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Close
          </Button>
        )}
      </Actions>
    </div>
  );
}

/** Settings: current PIN, then the new one twice. A wrong current PIN counts toward the lockout. */
export function SalaryPinChangeForm({
  onUnlocked,
  onLocked,
  onCancel,
}: {
  onUnlocked: (unlockedUntil: string) => void;
  onLocked: (lockedUntil: string | null) => void;
  onCancel?: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [currentProblem, setCurrentProblem] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const change = tsr.salaryPin.changePin.useMutation();

  function submit(event: FormEvent) {
    event.preventDefault();
    setCurrentProblem(null);
    setProblem(null);
    if (!/^\d{4,6}$/.test(current)) return setCurrentProblem('Enter your current PIN');
    const issue = newPinProblem(pin, confirm);
    if (issue) return setProblem(issue);
    if (pin === current) return setProblem('Choose a PIN different from your current one');
    change.mutate(
      { body: { currentPin: current, newPin: pin } },
      {
        onSuccess: (result) => {
          if (result.status === 200) onUnlocked(localUnlockEnd(result.body.unlockedForMs));
        },
        onError: (error) => {
          setCurrent('');
          const remaining = wrongPinAttemptsRemaining(error);
          if (remaining !== null) {
            setCurrentProblem(`Wrong PIN — ${remaining} ${remaining === 1 ? 'try' : 'tries'} left`);
            return;
          }
          const refusal = salaryPinRequiredDetails(error);
          if (refusal?.reason === 'locked') return onLocked(refusal.lockedUntil ?? null);
          setProblem(salaryPinErrorMessage(error, 'Could not change your PIN. Please try again.'));
        },
      },
    );
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      <SalaryPinField
        label="Current PIN"
        name="currentPin"
        value={current}
        onChange={(value) => {
          setCurrent(value);
          setCurrentProblem(null);
        }}
        error={currentProblem}
        autoFocus
        disabled={change.isPending}
      />
      <NewPinFields
        pin={pin}
        confirm={confirm}
        onPin={(value) => {
          setPin(value);
          setProblem(null);
        }}
        onConfirm={(value) => {
          setConfirm(value);
          setProblem(null);
        }}
        problem={problem}
        disabled={change.isPending}
        autoFocus={false}
      />
      <Actions>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" loading={change.isPending}>
          Change PIN
        </Button>
      </Actions>
    </form>
  );
}

/**
 * Forgot PIN. With a number on file: an SMS code to it, then a new PIN. With none (`reset.needsPhone`):
 * confirm it is them, add a number by SMS, then a new PIN (`SalaryPinNewPhoneFlow`). Either reset clears a
 * lockout and unlocks this session.
 */
export function SalaryPinForgotFlow({
  reset,
  onUnlocked,
  onLocked,
  onBack,
}: {
  reset: SalaryPinStatus['reset'];
  onUnlocked: (unlockedUntil: string) => void;
  /** A wrong password counts toward the PIN lockout; the parent shows the countdown. */
  onLocked?: (lockedUntil: string | null) => void;
  onBack: () => void;
}) {
  if (reset.needsPhone) {
    return (
      <SalaryPinNewPhoneFlow
        reset={reset}
        onUnlocked={onUnlocked}
        onLocked={onLocked}
        onBack={onBack}
      />
    );
  }
  return <SalaryPinPhoneOnFileFlow reset={reset} onUnlocked={onUnlocked} onBack={onBack} />;
}

/**
 * An SMS code to the person's own number on file, then a new PIN twice. Without phone verification on this
 * deployment, or with no number on file, there is nothing to send to, so it says who can help instead.
 */
function SalaryPinPhoneOnFileFlow({
  reset,
  onUnlocked,
  onBack,
}: {
  reset: SalaryPinStatus['reset'];
  onUnlocked: (unlockedUntil: string) => void;
  onBack: () => void;
}) {
  const [proof, setProof] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const resetPin = tsr.salaryPin.resetPin.useMutation();

  if (!reset.available || !reset.phone || !reset.phoneVerification) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-fg-muted">
          Resetting your PIN needs a verified mobile number, and none is available here. Ask your
          administrator for help.
        </p>
        <Actions>
          <Button type="button" variant="ghost" onClick={onBack}>
            Back
          </Button>
        </Actions>
      </div>
    );
  }

  if (!proof) {
    return (
      <div className="flex flex-col gap-4">
        <SalaryPinPhoneCode
          config={reset.phoneVerification}
          phone={reset.phone}
          onVerified={(value) => {
            setProblem(null);
            setProof(value);
          }}
        />
        {problem && <Banner tone="danger">{problem}</Banner>}
        <Actions>
          <Button type="button" variant="ghost" onClick={onBack}>
            Back
          </Button>
        </Actions>
      </div>
    );
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const issue = newPinProblem(pin, confirm);
    if (issue) return setProblem(issue);
    setProblem(null);
    resetPin.mutate(
      { body: { phoneProof: proof!, newPin: pin } },
      {
        onSuccess: (result) => {
          if (result.status === 200) onUnlocked(localUnlockEnd(result.body.unlockedForMs));
        },
        onError: (error) => {
          // A refused proof is spent or wrong: verify the phone again. Anything else keeps the proof.
          if (errorReason(error) === 'phone_unverified') setProof(null);
          setProblem(salaryPinErrorMessage(error, 'Could not reset your PIN. Please try again.'));
        },
      },
    );
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      <p className="text-sm text-fg-muted">
        Verified <span className="font-semibold text-fg">{maskedPinPhone(reset.phone)}</span>.
        Choose a new salary PIN.
      </p>
      <NewPinFields
        pin={pin}
        confirm={confirm}
        onPin={(value) => {
          setPin(value);
          setProblem(null);
        }}
        onConfirm={(value) => {
          setConfirm(value);
          setProblem(null);
        }}
        problem={problem}
        disabled={resetPin.isPending}
      />
      <Actions>
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" variant="primary" loading={resetPin.isPending}>
          Save new PIN
        </Button>
      </Actions>
    </form>
  );
}
